/**
 * Presentation helpers for factory entities.
 */

/**
 * The human-facing batch number: the trailing sequence of a batch SK like
 * `ENROLL#us3d#me@x.com#BATCH#0001` → `0001`. Returns the id unchanged when it
 * has no `BATCH#<seq>` suffix (already-short ids such as `p2-0021`).
 */
export function batchSeq(batchId: string): string {
  const m = batchId.match(/BATCH#([^#]+)$/);
  return m ? m[1] : batchId;
}

/** Primary automatic factory score. New segmentation scorecards identify it
 * explicitly; the nested Dice and legacy attribute score fallbacks keep saved
 * pre-deploy scorecards readable without turning a missing metric into zero. */
export function factoryComparisonScore(
  scorecard: Record<string, unknown> | null | undefined,
): number | null {
  if (scorecard?.status !== "ok") return null;
  if (Object.prototype.hasOwnProperty.call(scorecard, "comparison_score")) {
    const value = scorecard.comparison_score;
    return typeof value === "number" && Number.isFinite(value) ? value : null;
  }
  const mask = scorecard.mask;
  if (mask && typeof mask === "object" && !Array.isArray(mask)) {
    const dice = (mask as Record<string, unknown>).mean_dice;
    if (typeof dice === "number" && Number.isFinite(dice)) return dice;
  }
  const legacy = scorecard.overall_agreement;
  return typeof legacy === "number" && Number.isFinite(legacy) ? legacy : null;
}

export function factoryComparisonMetric(
  scorecard: Record<string, unknown> | null | undefined,
): string | null {
  const metric = scorecard?.comparison_metric;
  if (metric === "mask_dice") return "Mask Dice";
  if (metric === "attribute_agreement") return "Attribute agreement";
  const mask = scorecard?.mask;
  if (mask && typeof mask === "object" && !Array.isArray(mask)) {
    const dice = (mask as Record<string, unknown>).mean_dice;
    if (typeof dice === "number" && Number.isFinite(dice)) return "Mask Dice";
  }
  return factoryComparisonScore(scorecard) !== null ? "Attribute agreement" : null;
}
